/**
 * FP3-069 — Solo provider direct assistant invite modal.
 *
 * Replaces the FP3-059 "Request assistant access" gated flow. Calls
 * POST /api/auth/invite-assistant, which writes the invite to the
 * standard `invites` collection so the SuperAdmin Access & Invites
 * page picks it up for free. No SuperAdmin approval gate.
 */
import { useState } from "react";
import { toast } from "sonner";
import { UsersThree, X as XIcon, PaperPlaneTilt } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";

const TYPE_OPTIONS = [
  { key: "SLPA", label: "SLPA (Speech-Language Pathology Assistant)" },
  { key: "COTA", label: "COTA (Certified Occupational Therapy Assistant)" },
  { key: "PTA", label: "PTA (Physical Therapist Assistant)" },
  { key: "Other", label: "Other assistant provider" },
];

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  assistantType: "SLPA",
  note: "",
};

export default function InviteAssistantModal({
  open, onOpenChange, onInvited,
}) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => setForm(INITIAL_FORM);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const handleOpenChange = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const canSubmit =
    !!form.email.trim() && !!form.assistantType && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const r = await api.inviteAssistantAsSolo({
        email: form.email.trim(),
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        assistant_type: form.assistantType,
        note: form.note.trim(),
      });
      toast.success(
        `Assistant invite sent to ${r?.data?.email || form.email.trim()}.`
      );
      onInvited?.(r?.data || null);
      reset();
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        (typeof detail === "string" && detail)
          || e?.message
          || "Could not send invite."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="invite-assistant-modal"
        className="sm:max-w-md bg-white"
      >
        <DialogHeader>
          <DialogTitle className="text-[15px] flex items-center gap-2">
            <UsersThree
              size={16}
              weight="duotone"
              className="text-[#1E3F33]"
            />
            Invite assistant
          </DialogTitle>
          <DialogDescription className="text-[12.5px]">
            Send a direct invite to a supervised provider (SLPA, COTA,
            PTA) under your solo workspace. The invite goes to their
            email; they&rsquo;ll set their password on accept. Up to 2
            active or pending assistants at a time.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label
                htmlFor="ia-first-name"
                className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]"
              >
                First name
              </label>
              <Input
                id="ia-first-name"
                data-testid="invite-assistant-first-name"
                value={form.firstName}
                onChange={(e) => upd("firstName", e.target.value)}
                maxLength={80}
                placeholder="Jamie"
                className="mt-1 text-[12.5px]"
              />
            </div>
            <div>
              <label
                htmlFor="ia-last-name"
                className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]"
              >
                Last name
              </label>
              <Input
                id="ia-last-name"
                data-testid="invite-assistant-last-name"
                value={form.lastName}
                onChange={(e) => upd("lastName", e.target.value)}
                maxLength={80}
                placeholder="Doe"
                className="mt-1 text-[12.5px]"
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="ia-email"
              className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]"
            >
              Email <span className="text-[#A8453A]">*</span>
            </label>
            <Input
              id="ia-email"
              data-testid="invite-assistant-email"
              type="email"
              value={form.email}
              onChange={(e) => upd("email", e.target.value)}
              maxLength={180}
              placeholder="jamie@school.org"
              required
              className="mt-1 text-[12.5px]"
            />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]">
              Assistant type <span className="text-[#A8453A]">*</span>
            </p>
            <div
              className="mt-1 flex flex-wrap gap-1.5"
              data-testid="invite-assistant-type-group"
            >
              {TYPE_OPTIONS.map((opt) => {
                const active = form.assistantType === opt.key;
                return (
                  <button
                    type="button"
                    key={opt.key}
                    onClick={() => upd("assistantType", opt.key)}
                    data-testid={`invite-assistant-type-${opt.key}`}
                    aria-pressed={active}
                    className={`text-[11.5px] rounded-full px-3 py-1 border transition-colors ${
                      active
                        ? "bg-[#1E3F33] border-[#1E3F33] text-white"
                        : "bg-white border-[#E5E3DB] text-[#3F4756] hover:bg-[#F3F1EC]"
                    }`}
                    title={opt.label}
                  >
                    {opt.key}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label
              htmlFor="ia-note"
              className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]"
            >
              Note (optional)
            </label>
            <Textarea
              id="ia-note"
              data-testid="invite-assistant-note"
              value={form.note}
              onChange={(e) => upd("note", e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder="Welcome message, supervision details, etc. No PHI."
              className="mt-1 text-[12.5px]"
            />
          </div>
          <p className="text-[10.5px] text-[#9AA0A6] leading-relaxed">
            Invites are written to the platform&rsquo;s standard
            invite collection. Platform support sees every invite for
            visibility — no approval gate. Inviting does not start
            billing.
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
            data-testid="invite-assistant-cancel"
          >
            <XIcon size={12} className="mr-1" /> Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="invite-assistant-submit"
            className="bg-[#1E3F33] hover:bg-[#173127] text-white disabled:bg-[#1E3F33]/50"
          >
            <PaperPlaneTilt size={12} className="mr-1" weight="duotone" />
            {submitting ? "Sending…" : "Send invite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
